import { getAuthIdentityEpoch } from "./authTokenStore.js";
import { postChatJob } from "./chatJobs.js";
import { openAgentPolicyPilot } from "./agentPolicyNavigation.js";
import { baselineConflicts, emptyProposal, proposalAgents, proposalChanges, proposalDocument, editProposal, rebaseProposal } from "./controlProposal.js";
import { describeControlRejections } from "./controlValueContracts.js";
import { compileControlChanges } from "./controlDrafting.js";

const sessions = new Map();
const listeners = new Map();
const timers = new Map();
const restoredPilotSelection = (saved) => saved?.pilotSelection || { mode: Object.values(saved?.pilot || {}).some((selection) => selection.endpoints?.length) ? "per-agent" : "shared", endpoints: [] };
export const getProposal = (id) => sessions.get(id) || null;
export function subscribeProposal(id, listener) {
    if (!listeners.has(id)) listeners.set(id, new Set());
    listeners.get(id).add(listener);
    return () => listeners.get(id)?.delete(listener);
}
export function updateProposal(id, patch) {
    const current = getProposal(id);
    if (!current || (current.guard && !current.guard())) return null;
    const next = { ...current, ...patch, version: current.version + 1 };
    sessions.set(id, next);
    for (const listener of listeners.get(id) || []) listener();
    next.onChange?.();
    clearTimeout(timers.get(id));
    timers.set(id, setTimeout(() => { timers.delete(id); flushProposalSave(id).catch(() => {}); }, 400));
    return next;
}
export function proposalSnapshot(session) {
    return { proposal: session.proposal, pilot: session.pilot, pilotSelection: session.pilotSelection, started: session.started, activeAgent: session.activeAgent,
        submissions: session.submissions, previousRevisions: session.previousRevisions, needsReview: session.needsReview, baseline: session.baseline };
}
export async function flushProposalSave(id) {
    clearTimeout(timers.get(id)); timers.delete(id);
    const session = getProposal(id);
    if (session) await session.saveSnapshot(id, proposalSnapshot(session));
}
export function endProposal(id) {
    clearTimeout(timers.get(id)); timers.delete(id);
    sessions.delete(id);
    for (const listener of listeners.get(id) || []) listener();
}
export function openWorkspaceProposal({ today, canManage, context, chat, saved, sessionOptions, startPilot, readSource }) {
    const id = sessionOptions.id || crypto.randomUUID();
    const restored = (saved?.started || []).map((result) => ({ ...result, ...(result.unknown ? { recoveryRequired: true } : {}) }));
    if (saved?.activeAgent && !restored.some((result) => result.agentId === saved.activeAgent)) restored.push({ agentId: saved.activeAgent, ok: false, unknown: true, recoveryRequired: true, error: "Recover the interrupted request in Investigation." });
    const proposal = saved?.proposal || emptyProposal();
    sessions.set(id, { id, version: 0, runtimeToken: {}, today, canManage, context, proposal,
        baseline: saved?.baseline || Object.fromEntries(Object.entries(today).map(([agentId, state]) => [agentId, state.generation])),
        pilot: saved?.pilot || {}, pilotSelection: restoredPilotSelection(saved),
        started: restored, submissions: saved?.submissions || [], activeAgent: null,
        previousRevisions: saved?.previousRevisions || [],
        validation: {}, view: "review", needsReview: baselineConflicts(proposal, today).length ? "The current policy changed at proposed settings. Review and resolve the changes before starting a pilot." : saved?.needsReview || null,
        chatId: chat.chatId, startPilot, readSource, ...sessionOptions });
    return { sessionId: id, chatId: chat.chatId, ready: Promise.resolve(getProposal(id)) };
}
export const changesFor = (session, agentId) => proposalChanges(session.proposal, agentId).map((edit) => ({ pointer: edit.pointer, value: edit.afterFound ? edit.after : undefined }));
export const documentFor = (session, agentId) => proposalDocument(session.proposal, session.today, agentId);
export const changedAgents = (session) => proposalAgents(session.proposal);
export const pilotResultFor = (session, agentId) => session.started.find((result) => result.agentId === agentId);
export const pilotAgentLocked = (session, agentId) => !!(pilotResultFor(session, agentId)?.ok || pilotResultFor(session, agentId)?.unknown || session.today[agentId]?.openPilot);
export const remainingPilotAgents = (session) => changedAgents(session).filter((agentId) => !pilotAgentLocked(session, agentId));
export const pilotEndpointsFor = (session, agentId) => session.pilotSelection.mode === "shared" ? session.pilotSelection.endpoints
    : session.pilot[agentId]?.on === false ? [] : session.pilot[agentId]?.endpoints || [];
export const pilotAgentsToStart = (session) => remainingPilotAgents(session).filter((agentId) => pilotEndpointsFor(session, agentId).length);

export function showCurrentProposal(id, { reveal = false, view = "review" } = {}) {
    const session = updateProposal(id, { view });
    if (!session) return false;
    if (session.isChatBusy?.()) { updateProposal(id, { revealRequested: reveal || session.revealRequested }); return false; }
    const posted = postChatJob({ kind: "controlProposal", key: `${id}|active`, chatId: session.chatId, reveal: reveal || !!session.revealRequested });
    if (posted && session.revealRequested) updateProposal(id, { revealRequested: false });
    return posted;
}

export async function applyControlChanges(id, changes, expectedRevision) {
    const session = getProposal(id);
    if (!session || session.view === "starting" || !session.canManage || (session.guard && !session.guard())) throw new Error("Reopen the current proposal with configuration manage access.");
    const operations = compileControlChanges(session, changes, expectedRevision);
    return operations.length ? applyProposalEdits(id, operations, expectedRevision, changes) : session.proposal;
}

// Only compiled operations and historical imports enter this commit boundary.
// Validation runs before committing a batch, so failed edits never corrupt the saved candidate.
export async function applyProposalEdits(id, operations, expectedRevision, structuredChanges) {
    const session = getProposal(id);
    if (!session || session.view === "starting" || !session.canManage || (session.guard && !session.guard())) throw new Error("Reopen the current proposal with configuration manage access.");
    if (session.started.length && session.previousRevisions.length >= 10) throw new Error("Discard the local draft before preparing more revisions. Existing pilots are unaffected.");
    const candidate = editProposal(session.proposal, session.today, operations, expectedRevision);
    if (structuredChanges) {
        candidate.structuredChanges = [...(session.proposal.structuredChanges || []), ...structuredClone(structuredChanges)];
        // Historical exact-value drafts have no semantic requests to replay.
        // Retain those imported edits while replaying all new typed requests.
        candidate.importedEdits = session.proposal.importedEdits || (session.proposal.structuredChanges ? {} : session.proposal.edits);
        if (candidate.structuredChanges.length > 500 || JSON.stringify(candidate).length > 200_000) throw new Error("This draft has reached its supported size. Start a new local draft for additional changes.");
    }
    candidate.revision = Math.max(session.proposal.revision, ...session.previousRevisions.map((row) => row.proposal.revision)) + 1;
    const agents = [...new Set(operations.map((op) => op.agentId))];
    const validation = {};
    for (const agentId of agents) {
        const result = await session.validate(agentId, proposalDocument(candidate, session.today, agentId));
        if (result.rejections?.length) throw new Error(`The proposed ${agentId} settings were rejected: ${describeControlRejections(agentId, session.today[agentId]?.catalog, result.rejections)}`);
        validation[agentId] = { revision: candidate.revision, valid: true,
            reachability: { total: result.total ?? null, reachable: result.reachable ?? null, shadowed: result.shadowed ?? null, unsupported: result.unsupported ?? null, observedAt: result.observedAt ?? null } };
    }
    const current = getProposal(id);
    if (current?.runtimeToken !== session.runtimeToken || current.proposal.revision !== expectedRevision || (current.guard && !current.guard())) throw new Error("The proposal changed during validation. Read it again before editing.");
    const submitted = current.started.length > 0 || current.submissions.some((submission) => submission.revision === current.proposal.revision);
    updateProposal(id, { proposal: candidate, validation, view: "review", needsReview: session.needsReview,
        ...(submitted ? { previousRevisions: [...current.previousRevisions, submittedRevision(current)].slice(-10), started: [], submissions: [], pilot: {}, pilotSelection: { mode: "shared", endpoints: [] },
            baseline: Object.fromEntries(Object.entries(current.today).map(([agentId, state]) => [agentId, state.generation])) } : {}) });
    await flushProposalSave(id);
    return candidate;
}

export async function acceptCurrentBaseline(id) {
    const session = getProposal(id);
    if (!session || session.view === "starting" || session.started.length) throw new Error("Submitted candidates cannot change. Create a new draft revision instead.");
    let proposal;
    if (session.proposal.structuredChanges?.length) {
        proposal = { ...emptyProposal(), edits: structuredClone(session.proposal.importedEdits || {}) };
        const requests = session.proposal.structuredChanges;
        for (let offset = 0; offset < requests.length; offset += 100) {
            const operations = compileControlChanges({ ...session, proposal }, requests.slice(offset, offset + 100), proposal.revision);
            if (operations.length) proposal = editProposal(proposal, session.today, operations, proposal.revision);
        }
        proposal = { ...proposal, revision: session.proposal.revision + 1, structuredChanges: requests, importedEdits: session.proposal.importedEdits || {} };
    } else proposal = rebaseProposal(session.proposal, session.today);
    for (const agentId of proposalAgents(proposal)) {
        const result = await session.validate(agentId, proposalDocument(proposal, session.today, agentId));
        if (result.rejections?.length) throw new Error(`The settings are no longer supported: ${describeControlRejections(agentId, session.today[agentId]?.catalog, result.rejections)}. Revise the draft in chat.`);
    }
    if (getProposal(id)?.runtimeToken !== session.runtimeToken || getProposal(id).proposal.revision !== session.proposal.revision) throw new Error("The draft changed. Review again.");
    updateProposal(id, { proposal, needsReview: null, baseline: Object.fromEntries(Object.entries(session.today).map(([agentId, state]) => [agentId, state.generation])) });
    await flushProposalSave(id);
}
export function setPilot(id, agentId, patch) {
    const session = getProposal(id);
    if (!session || session.view === "starting" || pilotAgentLocked(session, agentId)) return;
    updateProposal(id, { pilotSelection: { ...session.pilotSelection, mode: "per-agent" },
        pilot: { ...session.pilot, [agentId]: { on: true, endpoints: [], ...session.pilot[agentId], ...patch } } });
}
export function setSharedPilotEndpoints(id, endpoints) {
    const session = getProposal(id);
    if (!session || session.view === "starting" || session.pilotSelection.mode !== "shared") return;
    updateProposal(id, { pilotSelection: { mode: "shared", endpoints } });
}
export function setPilotEndpointMode(id, mode, agentId) {
    const session = getProposal(id);
    if (!session || session.view === "starting" || session.pilotSelection.mode === mode || !["shared", "per-agent"].includes(mode)) return;
    const endpoints = pilotEndpointsFor(session, agentId);
    // Customizing starts from the shared selection; returning to shared uses
    // the currently visible agent's selection for all remaining pilots.
    updateProposal(id, { pilotSelection: { mode, endpoints }, ...(mode === "per-agent" ? {
        pilot: { ...session.pilot, ...Object.fromEntries(remainingPilotAgents(session).map((id) => [id, { on: true, endpoints }])) },
    } : {}) });
}
const submittedRevision = (session) => ({ proposal: session.proposal, started: session.started, submissions: session.submissions, pilot: session.pilot, pilotSelection: session.pilotSelection, baseline: session.baseline });
export function reviewProposalRevision(id, revision) {
    const session = getProposal(id), previous = session?.previousRevisions.find((row) => row.proposal.revision === revision);
    if (!previous || session.view === "starting") return;
    const changed = baselineConflicts(previous.proposal, session.today).length || proposalAgents(previous.proposal).some((agentId) => previous.baseline?.[agentId] && previous.baseline[agentId] !== session.today[agentId]?.generation);
    updateProposal(id, { ...previous, pilotSelection: restoredPilotSelection(previous), view: "review", previousRevisions: [...session.previousRevisions.filter((row) => row !== previous), submittedRevision(session)],
        needsReview: changed ? "This submitted revision uses an older policy baseline. Prepare a new draft revision before starting more pilots." : null });
}
export async function startPilots(id) {
    const session = getProposal(id);
    if (!session || session.view !== "pilot" || session.needsReview || !session.canManage || (session.guard && !session.guard())) return;
    const identity = getAuthIdentityEpoch();
    const agents = pilotAgentsToStart(session);
    if (!agents.length) return;
    updateProposal(id, { view: "starting", activeAgent: null });
    const results = session.started.filter((result) => !agents.includes(result.agentId));
    for (const agentId of agents) {
        if (getProposal(id)?.runtimeToken !== session.runtimeToken || (session.guard && !session.guard())) break;
        const submission = { agentId, revision: session.proposal.revision, document: JSON.stringify(documentFor(session, agentId)),
            expectedGeneration: session.baseline[agentId] || "0", endpointIds: pilotEndpointsFor(session, agentId).map((endpoint) => endpoint.id) };
        updateProposal(id, { activeAgent: agentId, started: [...results], submissions: [...getProposal(id).submissions.filter((row) => row.agentId !== agentId), submission] });
        let dispatched = false;
        try {
            await flushProposalSave(id);
            dispatched = true;
            const result = await session.startPilot(agentId, { document: submission.document, expectedGeneration: submission.expectedGeneration, endpointIds: submission.endpointIds });
            results.push({ agentId, ok: !!result?.release?.releaseId, releaseId: result?.release?.releaseId, endpoints: submission.endpointIds.length,
                warning: result?.investigationSaveError ? "The pilot started, but its case result was not saved. Its saved request can recover it." : "" });
            if (result?.release?.releaseId && getProposal(id)?.runtimeToken === session.runtimeToken) {
                const today = getProposal(id).today;
                updateProposal(id, { today: { ...today, [agentId]: { ...today[agentId], openPilot: true, openPilotReleaseId: result.release.releaseId } } });
            }
        } catch (error) {
            results.push({ agentId, ok: false, unknown: dispatched && error?.pilotOutcome !== "not_started", error: error.message });
        }
        if (getProposal(id)?.runtimeToken !== session.runtimeToken) return;
        updateProposal(id, { started: [...results], activeAgent: null });
        await flushProposalSave(id).catch(() => {});
    }
    if (getProposal(id)?.runtimeToken !== session.runtimeToken) return;
    updateProposal(id, { view: "review", started: results, activeAgent: null });
    await flushProposalSave(id).catch(() => {});
    const first = results.find((result) => result.ok && !session.started.some((prior) => prior.ok && prior.agentId === result.agentId));
    if (first && identity === getAuthIdentityEpoch()) openAgentPolicyPilot(first.agentId, first.releaseId);
}
